import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, unref as _unref, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "p-4 sm:p-6" }
const _hoisted_2 = { class: "bg-white dark:bg-black rounded-lg shadow-lg overflow-hidden border border-gray-200 dark:border-gray-700" }
const _hoisted_3 = { key: 0 }
const _hoisted_4 = {
  key: 1,
  class: "text-center py-16 px-6"
}
const _hoisted_5 = { class: "text-red-500 dark:text-red-400 mb-6 max-w-sm mx-auto" }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = {
  key: 3,
  class: "text-center py-16 px-6"
}
const _hoisted_8 = { class: "text-gray-500 dark:text-gray-400 mb-6 max-w-sm mx-auto" }
const _hoisted_9 = { key: 4 }

import { ref, computed, watch } from 'vue';
import { useToast } from 'vue-toastification';
import { useAdminCustomerStore } from '@/stores/adminCustomerStore';
import { useAuthStore } from '@/stores/auth';
import type { Customer, CustomerUpdateData, CustomerCategoryUpdateData } from '@/types/customer';
import CustomerFilters from '@/components/admin/CustomerFilters.vue';
import CustomerTable from '@/components/admin/CustomerTable.vue';
import CustomerPagination from '@/components/admin/CustomerPagination.vue';
import CustomerDetailsModal from '@/components/admin/CustomerDetailsModal.vue';
import CustomerEditModal from '@/components/admin/CustomerEditModal.vue';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal.vue';
import ConfirmationModal from '@/components/common/ConfirmationModal.vue';
import ResetPasswordModal from '@/components/admin/ResetPasswordModal.vue';
import CustomerCategoryModal from '@/components/admin/CustomerCategoryModal.vue';
import SkeletonLoader from '@/components/common/SkeletonLoader.vue';


export default /*@__PURE__*/_defineComponent({
  __name: 'CustomersView',
  setup(__props) {

const toast = useToast();
const customerStore = useAdminCustomerStore();
const authStore = useAuthStore();

// Local state
const searchQuery = ref('');
const statusFilter = ref('');
const brandFilter = ref(String(authStore.adminBrandId ?? ''));
const showDetailsModal = ref(false);
const showEditModal = ref(false);
const showDeleteModal = ref(false);
const showResetPasswordModal = ref(false);
const showCategoryModal = ref(false);
const selectedCustomer = ref<Customer | null>(null);
const customerToDelete = ref<Customer | null>(null);
const customerToResetPassword = ref<Customer | null>(null);
const customerToUpdateCategory = ref<Customer | null>(null);
const editModalError = ref<string | null>(null);

// Computed properties from store
const customers = computed(() => customerStore.customers);
const loading = computed(() => customerStore.loading);
const error = computed(() => customerStore.error);
const pagination = computed(() => customerStore.pagination);

// Methods
function applyFilters() {
  customerStore.setFilters({
    search: searchQuery.value,
    status: statusFilter.value,
    brand: brandFilter.value,
    page: 1,
    per_page: 10 // Explicitly set per_page to ensure it's always 10
  });
  customerStore.fetchCustomers();
}

function resetFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  brandFilter.value = String(authStore.adminBrandId ?? '');
  customerStore.resetFilters();
  customerStore.setFilters({ brand: String(authStore.adminBrandId ?? '') });
  customerStore.fetchCustomers();
}

function goToPreviousPage() {
  if (pagination.value.current_page > 1) {
    customerStore.setFilters({ page: pagination.value.current_page - 1 });
    customerStore.fetchCustomers();
  }
}

function goToNextPage() {
  if (pagination.value.current_page < pagination.value.last_page) {
    customerStore.setFilters({ page: pagination.value.current_page + 1 });
    customerStore.fetchCustomers();
  }
}

function goToPage(page: number) {
  customerStore.setFilters({ page });
  customerStore.fetchCustomers();
}

async function viewCustomer(customer: Customer) {
  try {
    // Fetch full customer details with vehicles and tires
    await customerStore.fetchCustomer(customer.user_id);
    selectedCustomer.value = customerStore.selectedCustomer;
    showDetailsModal.value = true;
  } catch (err) {
    toast.error('Failed to load customer details');
  }
}

function editCustomer(customer: Customer) {
  selectedCustomer.value = customer;
  showEditModal.value = true;
  showDetailsModal.value = false;
}

async function handleCustomerRefresh() {
  // Refresh the customer details after tire approval/rejection
  if (selectedCustomer.value) {
    try {
      await customerStore.fetchCustomer(selectedCustomer.value.user_id);
      selectedCustomer.value = customerStore.selectedCustomer;
    } catch (err) {
      toast.error('Failed to refresh customer details');
    }
  }
}

function openResetPasswordModal(customer: Customer) {
  customerToResetPassword.value = customer;
  showResetPasswordModal.value = true;
}

function closeResetPasswordModal() {
  showResetPasswordModal.value = false;
  customerToResetPassword.value = null;
}

async function confirmResetPassword() {
  if (!customerToResetPassword.value) return;

  try {
    await customerStore.resetCustomerPassword(customerToResetPassword.value.user_id);
    toast.success('Password reset link sent successfully to ' + customerToResetPassword.value.email);
    closeResetPasswordModal();
  } catch (err) {
    toast.error('Failed to send password reset link');
  }
}

function openDeleteModal(customer: Customer) {
  customerToDelete.value = customer;
  showDeleteModal.value = true;
}

function closeDeleteModal() {
  showDeleteModal.value = false;
  customerToDelete.value = null;
}

async function confirmDelete() {
  if (!customerToDelete.value) return;

  try {
    await customerStore.deleteCustomer(customerToDelete.value.user_id);
    toast.success('Customer deleted successfully');
    closeDeleteModal();
  } catch (err) {
    toast.error('Failed to delete customer');
  }
}

async function handleUpdateCustomer(data: CustomerUpdateData) {
  if (!selectedCustomer.value) return;

  editModalError.value = null; // Clear previous errors

  try {
    await customerStore.updateCustomer(selectedCustomer.value.user_id, data);
    toast.success('Customer updated successfully');
    closeEditModal();
  } catch (err: any) {
    // Extract user-friendly error message
    let errorMessage = 'Failed to update customer'

    if (err.response?.data?.message) {
      // Use the specific error message from backend
      errorMessage = err.response.data.message
    } else if (err.response?.data?.errors) {
      // If there are field-specific errors, get the first one
      const errors = err.response.data.errors
      const firstErrorKey = Object.keys(errors)[0]
      if (firstErrorKey && errors[firstErrorKey]) {
        errorMessage = Array.isArray(errors[firstErrorKey])
          ? errors[firstErrorKey][0]
          : errors[firstErrorKey]
      }
    }

    // Set error for modal display
    editModalError.value = errorMessage

    // Show error in toast
    toast.error(errorMessage)

    // Don't close modal - let user see the error and fix it
  }
}

function openCategoryModal(customer: Customer) {
  customerToUpdateCategory.value = customer;
  showCategoryModal.value = true;
}

function closeCategoryModal() {
  showCategoryModal.value = false;
  customerToUpdateCategory.value = null;
}

async function handleUpdateCategory(data: CustomerCategoryUpdateData) {
  if (!customerToUpdateCategory.value) return;
  try {
    await customerStore.updateCustomerCategory(customerToUpdateCategory.value.user_id, data);
    toast.success('Customer category updated successfully');
    closeCategoryModal();
  } catch (err: any) {
    toast.error(err.response?.data?.message || 'Failed to update category');
  }
}

function closeDetailsModal() {
  showDetailsModal.value = false;
  selectedCustomer.value = null;
}

function closeEditModal() {
  showEditModal.value = false;
  selectedCustomer.value = null;
  editModalError.value = null;
  customerStore.clearError();
}

// Lifecycle
watch(
  () => authStore.adminBrandId,
  (newBrandId) => {
    brandFilter.value = String(newBrandId ?? '');
    customerStore.resetFilters();
    customerStore.setFilters({ brand: String(newBrandId ?? '') });
    customerStore.fetchCustomers();
  },
  { immediate: true }
);

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createElementVNode("div", _hoisted_1, [
      _cache[7] || (_cache[7] = _createElementVNode("div", { class: "mb-6 sm:mb-8" }, [
        _createElementVNode("h1", { class: "text-2xl font-bold text-gray-900 dark:text-white" }, "Customer Management")
      ], -1)),
      _createVNode(CustomerFilters, {
        search: searchQuery.value,
        "onUpdate:search": _cache[0] || (_cache[0] = ($event: any) => ((searchQuery).value = $event)),
        status: statusFilter.value,
        "onUpdate:status": _cache[1] || (_cache[1] = ($event: any) => ((statusFilter).value = $event)),
        brand: brandFilter.value,
        "onUpdate:brand": _cache[2] || (_cache[2] = ($event: any) => ((brandFilter).value = $event)),
        onApply: applyFilters,
        onReset: resetFilters
      }, null, 8, ["search", "status", "brand"]),
      _createElementVNode("div", _hoisted_2, [
        (loading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
              _createVNode(SkeletonLoader, {
                type: "table",
                rows: 5,
                columns: 6
              }),
              _createVNode(SkeletonLoader, {
                type: "card",
                rows: 5
              })
            ]))
          : (error.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                _cache[4] || (_cache[4] = _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  class: "mx-auto h-16 w-16 text-red-400 mb-4",
                  fill: "none",
                  viewBox: "0 0 24 24",
                  stroke: "currentColor"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "1",
                    d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ], -1)),
                _cache[5] || (_cache[5] = _createElementVNode("h3", { class: "text-lg font-medium text-gray-900 dark:text-white mb-2" }, "Error loading customers", -1)),
                _createElementVNode("p", _hoisted_5, _toDisplayString(error.value), 1),
                _createElementVNode("button", {
                  class: "bg-primary hover:bg-primary-hover text-white px-6 py-3 rounded-lg transition-colors duration-200 shadow-sm",
                  onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(customerStore).fetchCustomers()))
                }, " Try Again ")
              ]))
            : (customers.value.length > 0)
              ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                  _createVNode(CustomerTable, {
                    customers: customers.value,
                    loading: loading.value,
                    onView: viewCustomer,
                    onEdit: editCustomer,
                    onResetPassword: openResetPasswordModal,
                    onDelete: openDeleteModal,
                    onUpdateCategory: openCategoryModal
                  }, null, 8, ["customers", "loading"])
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_7, [
                  _cache[6] || (_cache[6] = _createElementVNode("svg", {
                    xmlns: "http://www.w3.org/2000/svg",
                    class: "mx-auto h-16 w-16 text-gray-400 mb-4",
                    fill: "none",
                    viewBox: "0 0 24 24",
                    stroke: "currentColor"
                  }, [
                    _createElementVNode("path", {
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round",
                      "stroke-width": "1",
                      d: "M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    })
                  ], -1)),
                  _createElementVNode("p", _hoisted_8, _toDisplayString(searchQuery.value || statusFilter.value ? 'No customers match your search criteria.' : 'No customers found.'), 1)
                ])),
        (customers.value.length > 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
              _createVNode(CustomerPagination, {
                "current-page": pagination.value.current_page,
                "last-page": pagination.value.last_page,
                "per-page": pagination.value.per_page,
                total: pagination.value.total,
                onPrev: goToPreviousPage,
                onNext: goToNextPage,
                onGoto: goToPage
              }, null, 8, ["current-page", "last-page", "per-page", "total"])
            ]))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(CustomerDetailsModal, {
      "is-open": showDetailsModal.value,
      customer: selectedCustomer.value,
      onClose: closeDetailsModal,
      onEdit: editCustomer,
      onRefresh: handleCustomerRefresh
    }, null, 8, ["is-open", "customer"]),
    _createVNode(CustomerEditModal, {
      "is-open": showEditModal.value,
      customer: selectedCustomer.value,
      loading: loading.value,
      error: editModalError.value,
      onClose: closeEditModal,
      onSubmit: handleUpdateCustomer
    }, null, 8, ["is-open", "customer", "loading", "error"]),
    _createVNode(DeleteConfirmationModal, {
      "is-open": showDeleteModal.value,
      "is-loading": loading.value,
      "entity-name": customerToDelete.value ? `${customerToDelete.value.first_name} ${customerToDelete.value.last_name}` : '',
      "entity-type": "customer",
      onClose: closeDeleteModal,
      onConfirm: confirmDelete
    }, null, 8, ["is-open", "is-loading", "entity-name"]),
    _createVNode(ResetPasswordModal, {
      "is-open": showResetPasswordModal.value,
      customer: customerToResetPassword.value,
      loading: loading.value,
      error: error.value,
      onClose: closeResetPasswordModal,
      onConfirm: confirmResetPassword
    }, null, 8, ["is-open", "customer", "loading", "error"]),
    _createVNode(CustomerCategoryModal, {
      "is-open": showCategoryModal.value,
      customer: customerToUpdateCategory.value,
      loading: loading.value,
      onClose: closeCategoryModal,
      onSubmit: handleUpdateCategory
    }, null, 8, ["is-open", "customer", "loading"])
  ], 64))
}
}

})